import React, { useEffect, useMemo, useState } from "react";
import { createKpi, listKpis, exportKpisCsv } from "../../api/kpi.api";
import KpiBadge from "../../components/kpi/KpiBadge";
import KpiDetailDrawer from "../../components/kpi/KpiDetailDrawer";
import { KPICopy } from "../../copy/kpiCopy";
import { KPI_CATEGORY_OPTIONS, getKpiSubcategoryOptions } from "../../constants/kpiOptions";
import { useAuth } from "../../useAuth";
import { RoleEnum, hasRole } from "../../constants/roles";
import { canEditKpis } from "../../accessControl";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import PageState from "../../ui/PageState";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";
import Table from "../../ui/Table";
import StatusBadge from "../../ui/StatusBadge";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";
import { colors, fontSizes, radius, shadows, spacing, typography } from "../../ui/tokens";
import {
  createKpiUnitFieldState,
  formatKpiValueWithUnit,
  KPI_UNIT_CUSTOM_MAX_LENGTH,
  KPI_UNIT_OPTIONS,
  KPI_UNIT_OTHER_VALUE,
  resolveKpiUnitApiValue,
  validateKpiUnitFieldState,
} from "../../utils/kpiUnits";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardBodyStyle,
  surfaceCardStyle,
  toolbarStyle as sharedToolbarStyle,
} from "../../ui/pagePatterns";

export default function KpiList() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [drawerKpiId, setDrawerKpiId] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    category: "",
    subcategory: "",
    frequency: "",
    target_value: "",
    requires_approval: false,
  });
  const [createUnitField, setCreateUnitField] = useState(() => createKpiUnitFieldState(null));
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const { user } = useAuth() || {};
  const subcategoryOptions = useMemo(
    () => getKpiSubcategoryOptions(createForm.category),
    [createForm.category]
  );
  const canExport = useMemo(() => {
    return hasRole(user?.role, [RoleEnum.ADMIN, RoleEnum.MANAGER, RoleEnum.EXEC]);
  }, [user]);

  const canEdit = useMemo(() => {
    return canEditKpis(user);
  }, [user]);

  const seedKpis = async () => {
    const seededKey = "kpi_seeded_v1";
    if (!canEdit) {
      return;
    }
    if (localStorage.getItem(seededKey)) {
      return;
    }
    const seeds = [
      {
        name: "Incident Response Time",
        description: "Average time to resolve critical incidents.",
        unit: "hours",
        category: "Incidents",
        subcategory: "Response Time",
        frequency: "weekly",
        target_value: 24,
      },
      {
        name: "Control Coverage",
        description: "Percentage of risks with at least one control linked.",
        unit: "%",
        category: "Controls",
        subcategory: "Coverage",
        frequency: "monthly",
        target_value: 80,
      },
      {
        name: "Audit Findings Closed",
        description: "Count of audit findings closed this period.",
        unit: "count",
        category: "Audits",
        subcategory: "Findings",
        frequency: "monthly",
        target_value: 10,
      },
      {
        name: "Vendor Risk Reviews",
        description: "Third-party vendors reviewed on schedule.",
        unit: "count",
        category: "Vendors",
        subcategory: "Risk Reviews",
        frequency: "quarterly",
        target_value: 5,
      },
      {
        name: "Policy Acknowledgement",
        description: "Percentage of staff acknowledging required policies.",
        unit: "%",
        category: "Policies",
        subcategory: "Acknowledgement",
        frequency: "monthly",
        target_value: 95,
      },
    ];
    try {
      for (const seed of seeds) {
        await createKpi(seed);
      }
      localStorage.setItem(seededKey, "true");
    } catch (err) {
      console.error("Seed KPIs failed", err);
    }
  };

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await listKpis({ search, status });
      const data = res.data || [];
      if (data.length === 0) {
        await seedKpis();
        const seededRes = await listKpis({ search, status });
        setItems(seededRes.data || []);
      } else {
        setItems(data);
      }
    } catch (err) {
      setItems([]);
      setLoadError(err?.response?.data?.detail || err?.message || "Unable to load KPI records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetCreateState = () => {
    setCreateForm({
      name: "",
      description: "",
      category: "",
      subcategory: "",
      frequency: "",
      target_value: "",
      requires_approval: false,
    });
    setCreateUnitField(createKpiUnitFieldState(null));
    setCreateError("");
  };

  const handleCreateUnitChange = (value) => {
    setCreateUnitField((prev) => ({
      ...prev,
      selectedKey: value,
      isDirty: true,
    }));
    setCreateError("");
  };

  const handleCreateCustomUnitChange = (value) => {
    setCreateUnitField((prev) => ({
      ...prev,
      customValue: value,
      isDirty: true,
    }));
    setCreateError("");
  };

  const handleCreate = async () => {
    if (!canEdit) return;
    if (!createForm.name.trim()) {
      setCreateError("Name is required.");
      return;
    }
    const unitError = validateKpiUnitFieldState(createUnitField);
    if (unitError) {
      setCreateError(unitError);
      return;
    }
    try {
      setCreateLoading(true);
      setCreateError("");
      const payload = {
        name: createForm.name.trim(),
        description: createForm.description || null,
        unit: resolveKpiUnitApiValue(createUnitField),
        category: createForm.category || null,
        subcategory: createForm.subcategory || null,
        frequency: createForm.frequency || null,
        target_value: createForm.target_value ? Number(createForm.target_value) : null,
        requires_approval: Boolean(createForm.requires_approval),
      };
      const res = await createKpi(payload);
      setShowCreate(false);
      resetCreateState();
      load();
      if (res?.data?.id) {
        setDrawerKpiId(res.data.id);
      }
    } catch (err) {
      console.error("Create KPI failed", err);
      setCreateError("Create KPI failed. Please try again.");
    } finally {
      setCreateLoading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportAll = async () => {
    if (!canExport) return;
    try {
      const res = await exportKpisCsv();
      downloadBlob(res.data, "kpis-export.csv");
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  const tableColumns = [
    {
      id: "name",
      header: "Name",
      accessor: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
          <span>{row.name}</span>
          {String(row.status || "").toLowerCase() === "archived" && (
            <StatusBadge variant="status" value="archived" label="Archived" />
          )}
        </div>
      ),
    },
    {
      id: "latest",
      header: "Latest",
      accessor: (row) =>
        row.latest_value !== null && row.latest_value !== undefined
          ? formatKpiValueWithUnit(row.latest_value, row.unit)
          : "--",
    },
    {
      id: "rag",
      header: "Status (Red/Amber/Green)",
      accessor: (row) => <KpiBadge value={row.latest_rag} />,
    },
    { id: "owner", header: "Owner", accessor: (row) => row.owner_id || "--" },
    {
      id: "category",
      header: "Category",
      accessor: (row) => [row.category, row.subcategory].filter(Boolean).join(" / ") || "--",
    },
    {
      id: "updated",
      header: "Updated",
      accessor: (row) => (row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "--"),
    },
  ];

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderStyle}>
        <div style={pageHeaderTitleBlockStyle}>
          <h2 style={pageTitleStyle}>{KPICopy.listTitle}</h2>
          <p style={pageSubtitleStyle}>{KPICopy.listIntro}</p>
          <p style={pageSubtitleStyle}>{KPICopy.listIntroSecondary}</p>
        </div>
        <div style={{ ...pageHeaderActionsStyle, flexDirection: "column", alignItems: "flex-end" }}>
          <div style={pageHeaderActionsStyle}>
            {canExport && (
              <Button onClick={handleExportAll} variant="secondary">
                Export all (CSV)
              </Button>
            )}
            <Button
              onClick={() => {
                if (!canEdit) return;
                resetCreateState();
                setShowCreate(true);
              }}
              disabled={!canEdit}
              title={canEdit ? undefined : "Read-only access"}
              variant="primary"
            >
              Add KPI
            </Button>
          </div>
          {canExport && (
            <div style={{ fontSize: fontSizes.sm, color: colors.textMuted }}>
              Exports may contain sensitive data.
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          ...surfaceCardStyle,
          background: colors.surfaceSubtle,
          boxShadow: shadows.xs,
        }}
      >
        <div style={surfaceCardBodyStyle}>
          <div style={{ display: "grid", gap: spacing.sm }}>
            <div style={{ fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: colors.textPrimary }}>
              KPI Workspace
            </div>
            <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
              {KPICopy.betaNotice}
            </div>
            <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{KPICopy.notAvailable}</div>
            {!canEdit && (
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{KPICopy.readOnlyNotice}</div>
            )}
          </div>
        </div>
      </div>

      <div style={surfaceCardStyle}>
        <div style={surfaceCardBodyStyle}>
          <div style={{ ...sharedToolbarStyle, alignItems: "flex-end" }}>
            <Input
              label="Search KPIs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search KPIs"
              style={{ flex: "1 1 280px", minWidth: 240 }}
            />
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={[
                { value: "", label: "All" },
                { value: "active", label: "Active" },
                { value: "paused", label: "Paused" },
                { value: "archived", label: "Archived" },
              ]}
              style={{ width: 180, maxWidth: "100%" }}
            />
            <Button onClick={load} variant="secondary">
              Apply Filters
            </Button>
          </div>
        </div>
      </div>

      {loadError ? (
        <PageState
          state="error"
          align="left"
          title="KPI records are unavailable"
          description={`${loadError} Refresh the workspace to try again.`}
          action={
            <Button type="button" variant="secondary" onClick={load}>
              Refresh
            </Button>
          }
        />
      ) : loading ? (
        <PageState
          state="loading"
          align="left"
          title="Loading KPIs"
          description="Glyder is preparing KPI definitions, latest values, and threshold status."
        />
      ) : items.length === 0 ? (
        <PageState
          state="empty"
          align="left"
          title={KPICopy.listEmptyTitle}
          description={KPICopy.listEmptyBody}
          actions={
            <>
              <Button
                onClick={() => canEdit && setShowCreate(true)}
                disabled={!canEdit}
                title={canEdit ? undefined : "Read-only access"}
                variant="primary"
              >
                {KPICopy.listEmptyCta}
              </Button>
              <span style={{ fontSize: fontSizes.sm, color: colors.textMuted }}>{KPICopy.listEmptyCreateHint}</span>
            </>
          }
        />
      ) : (
        <div style={{ ...surfaceCardStyle, overflow: "hidden" }}>
          <Table
            columns={tableColumns}
            data={items}
            onRowClick={(row) => setDrawerKpiId(row.id)}
            getRowKey={(row) => row.id}
            getRowProps={(row) => ({
              style:
                String(row.latest_rag || "").toLowerCase() === "stale"
                  ? { background: colors.warningSubtle }
                  : undefined,
            })}
            emptyState={KPICopy.listEmptyTitle}
          />
        </div>
      )}

      {showCreate && (
        <ModalA11yWrapper open={showCreate} onClose={() => setShowCreate(false)} labelledBy="kpi-create-title">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCreate();
            }}
            style={{
              background: colors.surface,
              padding: spacing.xl + spacing.sm,
              width: "min(520px, 95vw)",
              borderRadius: radius.lg,
              border: `1px solid ${colors.border}`,
              boxShadow: shadows.lg,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: spacing.md,
              }}
            >
              <h3 id="kpi-create-title" style={{ margin: 0 }}>
                Add KPI
              </h3>
              <Button
                type="button"
                onClick={() => setShowCreate(false)}
                variant="ghost"
                aria-label="Close"
              >
                X
              </Button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: spacing.md,
              }}
            >
              <div style={{ gridColumn: "1 / -1" }}>
                <Input
                  label="Name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <Select
                label="Unit"
                value={createUnitField.selectedKey}
                onChange={(e) => handleCreateUnitChange(e.target.value)}
                options={KPI_UNIT_OPTIONS}
              />
              {createUnitField.selectedKey === KPI_UNIT_OTHER_VALUE && (
                <Input
                  label="Custom unit"
                  value={createUnitField.customValue}
                  onChange={(e) => handleCreateCustomUnitChange(e.target.value)}
                  error={createUnitField.isDirty ? validateKpiUnitFieldState(createUnitField) : ""}
                  maxLength={KPI_UNIT_CUSTOM_MAX_LENGTH}
                  placeholder="Enter custom unit"
                />
              )}
              <Select
                label="Category"
                value={createForm.category}
                onChange={(e) =>
                  setCreateForm((prev) => {
                    const nextCategory = e.target.value;
                    const options = getKpiSubcategoryOptions(nextCategory);
                    const hasCurrent = options.some((opt) => opt.value === prev.subcategory);
                    return {
                      ...prev,
                      category: nextCategory,
                      subcategory: hasCurrent ? prev.subcategory : "",
                    };
                  })
                }
                options={KPI_CATEGORY_OPTIONS}
              />
              <Select
                label="Subcategory"
                value={createForm.subcategory}
                onChange={(e) => setCreateForm((p) => ({ ...p, subcategory: e.target.value }))}
                disabled={!createForm.category}
                options={[{ value: "", label: "None" }, ...subcategoryOptions]}
              />
              <Select
                label="Frequency"
                value={createForm.frequency}
                onChange={(e) => setCreateForm((p) => ({ ...p, frequency: e.target.value }))}
                options={[
                  { value: "", label: "None" },
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                  { value: "quarterly", label: "Quarterly" },
                ]}
              />
              <Input
                label="Target value"
                type="number"
                value={createForm.target_value}
                onChange={(e) => setCreateForm((p) => ({ ...p, target_value: e.target.value }))}
              />
              <div style={{ gridColumn: "1 / -1" }}>
                <Textarea
                  label="Description"
                  value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <label
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.sm,
                  fontSize: fontSizes.md,
                }}
              >
                <input
                  type="checkbox"
                  checked={createForm.requires_approval}
                  onChange={(e) => setCreateForm((p) => ({ ...p, requires_approval: e.target.checked }))}
                />
                Requires approval for measurements
              </label>
            </div>
            {createError && (
              <PageState
                state="error"
                align="left"
                compact
                title="Could not create KPI"
                description={createError}
                style={{ marginTop: spacing.sm }}
              />
            )}
            <div
              style={{
                marginTop: spacing.md,
                display: "flex",
                justifyContent: "flex-end",
                gap: spacing.sm,
              }}
            >
              <Button
                onClick={() => {
                  setShowCreate(false);
                  resetCreateState();
                }}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createLoading} variant="primary">
                {createLoading ? "Creating..." : "Create KPI"}
              </Button>
            </div>
          </form>
        </ModalA11yWrapper>
      )}
      <KpiDetailDrawer
        kpiId={drawerKpiId}
        onClose={() => setDrawerKpiId(null)}
        onUpdated={load}
      />
    </div>
  );
}
